// The Actions graph at three levels of detail, so organisations with many models can be read: a node for each model,
// joined where one joins or uses information from another, drawn as the home screen's model diagram is; each model's
// main flow, with the work off it gathered up; and every action. The two step levels are built here, pure so they can
// be tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { EDGE_TYPE, GraphData, NODE_SUPERTYPE, NODE_TYPE } from '../common/linqset-model';
import { ACTION_TYPE } from '../common/organisation';
import { mainPath } from './main-path';

export type GraphLevel = 'models' | 'flow' | 'actions';

export const GRAPH_LEVELS: { level: GraphLevel; label: string; description: string }[] = [
    { level: 'models', label: 'Models', description: 'A node for each model, joined where one joins or uses information from another, as on the home screen' },
    { level: 'flow', label: 'Main flow', description: "Each model's main flow, with the work off it gathered up where it leaves or joins" },
    { level: 'actions', label: 'Actions', description: 'Every action' },
];

// beyond either of these the graph opens at model level, as every action is slow to lay out and hard to read
export const LARGE_GRAPH = { models: 12, actions: 400 };

// a node standing for several: the work off a model's main flow, gathered up
export interface GroupedNode extends AnalysisNode {
    members: AnalysisNode[];
}

export const isGrouped = (node: AnalysisNode | undefined): node is GroupedNode => Array.isArray((node as GroupedNode | undefined)?.members);

// what a graph opens at: the models for a large organisation, every action for a small one
export const defaultGraphLevel = (data: { nodes: AnalysisNode[] }, modelKeys: string[]): GraphLevel => {
    const shown = new Set(modelKeys);
    const actions = data.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && shown.has(n.modelKey)).length;
    return shown.size > LARGE_GRAPH.models || actions > LARGE_GRAPH.actions ? 'models' : 'actions';
};

// the most pieces of information an edge names before the rest are counted
const MAX_NAMES = 2;
const names = (all: string[]) => {
    const unique = [...new Set(all.filter(n => !!n))];
    return unique.length <= MAX_NAMES ? unique.join(', ') : `${unique.slice(0, MAX_NAMES).join(', ')} and ${unique.length - MAX_NAMES} more`;
};

const sum = (nodes: AnalysisNode[], field: (n: AnalysisNode) => number | undefined) => nodes.reduce((t, n) => t + (field(n) || 0), 0);
const max = (nodes: AnalysisNode[], field: (n: AnalysisNode) => number | undefined) => Math.max(0, ...nodes.map(n => field(n) || 0));

// the people, systems and figures of the steps a node stands for. Hours, costs and carbon add up; a run of steps
// carries the same items and value through each, so those are the most of any one step
const groupOf = (key: string, name: string, modelKey: string, members: AnalysisNode[], type: NODE_TYPE, isStarting: boolean): GroupedNode => {
    const actions = members.filter(n => n.superType === NODE_SUPERTYPE.ACTION);
    const roles = new Map<string, Set<string>>();
    actions.forEach(a => (a.roles || []).forEach(r => {
        const systems = roles.get(r._key) || roles.set(r._key, new Set()).get(r._key)!;
        (r.systemKeys || []).forEach(s => systems.add(s));
    }));
    const types = new Set(actions.map(a => a.actionType));
    const last = actions[actions.length - 1];
    return {
        _key: key, modelKey, name, superType: NODE_SUPERTYPE.ACTION, type, members,
        durationSeconds: sum(actions, n => n.durationSeconds),
        roles: [...roles.entries()].map(([k, s]) => ({ _key: k, systemKeys: [...s] })),
        systems: [...new Set(actions.flatMap(a => a.systems || []))],
        isStarting,
        actionType: types.size === 1 ? actions[0]?.actionType : ACTION_TYPE.BOTH,
        numPeople: max(actions, n => n.numPeople),
        value: max(actions, n => n.value),
        items: max(actions, n => n.items),
        capacity: sum(actions, n => n.capacity),
        hours: sum(actions, n => n.hours),
        revenue: max(actions, n => n.revenue),
        // contributions are each step's own share, so they add up
        contributedValue: sum(actions, n => n.contributedValue),
        contributedRevenue: sum(actions, n => n.contributedRevenue),
        cost: sum(actions, n => n.cost),
        carbon: sum(actions, n => n.carbon),
        totalHours: last?.totalHours || 0,
        totalCost: last?.totalCost || 0,
        totalCarbon: last?.totalCarbon || 0,
        outputsSupported: max(actions, n => n.outputsSupported),
        friction: sum(actions, n => n.friction),
        userValue: 0, userRevenue: 0, derivedValue: 0, consumers: 0, consumerModels: 0, outcomesReached: 0, externalConsumers: 0,
        exceptionWork: actions.some(a => a.exceptionWork),
        onDetour: actions.some(a => a.onDetour),
        detourShare: 0,
        centrality: max(members, n => n.centrality),
    } as GroupedNode;
};

// the edges between the nodes standing for others: one for each pair, carrying what all of theirs carried, and none
// inside a node
const edgesBetween = (edges: AnalysisEdge[], groupKey: (key: string) => string | undefined, modelOf: (key: string) => string) => {
    const merged = new Map<string, { edge: AnalysisEdge; names: string[]; types: Set<EDGE_TYPE> }>();
    edges.forEach(e => {
        const from = groupKey(e.from), to = groupKey(e.to);
        if (!from || !to || from === to) return;
        const key = `${from}>${to}`;
        const existing = merged.get(key);
        if (existing) {
            existing.edge.items += e.items || 0;
            existing.edge.percentage += e.percentage || 0;
            existing.edge.proportion += e.proportion || 0;
            existing.names.push(e.informationName);
            existing.types.add(e.type);
            return;
        }
        merged.set(key, { edge: { ...e, _key: key, from, to, modelKey: modelOf(from), items: e.items || 0, percentage: e.percentage || 0, proportion: e.proportion || 0 },
            names: [e.informationName], types: new Set([e.type]) });
    });
    return [...merged.values()].map(({ edge, names: carried, types }) => ({
        ...edge,
        informationName: names(carried),
        // information only used elsewhere carries no items; anything else that passes makes it a flow of work
        type: types.size === 1 ? edge.type : EDGE_TYPE.ACTION_SUMMARY,
        proportion: Math.min(1, edge.proportion),
    }));
};

// each model's main flow, step by step, with the work off it gathered up: the main flow starts at the step most items
// start at and follows, at each branch, the connection most items take, to where the work ends. The steps and
// information off it go into one node for each place they leave it ("Other routes from …", or "Exception work from …"
// when all of it is exception work), or for work that comes in from another start, the place it joins it ("Routes
// into …"). Connections between models stay between whichever nodes hold their ends
const mainFlowLevel = (nodes: AnalysisNode[], edges: AnalysisEdge[]) => {
    const byKey = new Map(nodes.map(n => [n._key, n]));
    const sameModel = (e: AnalysisEdge) => e.from !== e.to && byKey.get(e.from)?.modelKey === byKey.get(e.to)?.modelKey;
    const outgoing = new Map<string, AnalysisEdge[]>(), incoming = new Map<string, AnalysisEdge[]>();
    edges.filter(sameModel).forEach(e => {
        (outgoing.get(e.from) || outgoing.set(e.from, []).get(e.from)!).push(e);
        (incoming.get(e.to) || incoming.set(e.to, []).get(e.to)!).push(e);
    });

    const groupOfKey = new Map<string, string>();
    const result: AnalysisNode[] = [];
    const byModel = new Map<string, AnalysisNode[]>();
    nodes.forEach(n => (byModel.get(n.modelKey) || byModel.set(n.modelKey, []).get(n.modelKey)!).push(n));

    byModel.forEach(modelNodes => {
        // the main flow: from the busiest start, along the busiest connection at each step
        const starts = modelNodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && (n.isStarting || !(incoming.get(n._key) || []).length));
        const main = mainPath(starts, n => n.items || 0, n => (outgoing.get(n._key) || []).filter(e => byKey.has(e.to))
            .map(e => ({ to: byKey.get(e.to)!, items: e.items || 0, share: e.percentage || 0 }))).nodes;
        main.forEach(n => { groupOfKey.set(n._key, n._key); result.push(n); });

        // the work off it, gathered by where it leaves the main flow (nearest the start first), then by where work
        // from elsewhere joins it
        const gathered = new Map<string, { key: string; from?: AnalysisNode; into?: AnalysisNode; members: AnalysisNode[] }>();
        const gather = (seed: AnalysisNode, along: (key: string) => string[], group: { key: string; from?: AnalysisNode; into?: AnalysisNode; members: AnalysisNode[] }) => {
            const queue = along(seed._key).filter(k => !groupOfKey.has(k));
            while (queue.length) {
                const key = queue.shift()!;
                if (groupOfKey.has(key)) continue;
                groupOfKey.set(key, group.key);
                group.members.push(byKey.get(key)!);
                queue.push(...along(key).filter(k => !groupOfKey.has(k)));
            }
            if (group.members.length) gathered.set(group.key, group);
        };
        const after = (key: string) => (outgoing.get(key) || []).map(e => e.to);
        const before = (key: string) => (incoming.get(key) || []).map(e => e.from);
        main.forEach(n => gather(n, after, { key: `off:${n._key}`, from: n, members: [] }));
        [...main].reverse().forEach(n => gather(n, before, { key: `into:${n._key}`, into: n, members: [] }));
        // anything connected to neither: work of its own in the model
        const rest = modelNodes.filter(n => !groupOfKey.has(n._key));
        if (rest.length) {
            const key = `other:${rest[0].modelKey}`;
            rest.forEach(n => groupOfKey.set(n._key, key));
            gathered.set(key, { key, members: rest });
        }

        gathered.forEach(({ key, from, into, members }) => {
            const steps = members.filter(m => m.superType === NODE_SUPERTYPE.ACTION);
            const outputs = members.filter(m => m.superType === NODE_SUPERTYPE.INFORMATION).length;
            const outcomes = members.filter(m => m.superType === NODE_SUPERTYPE.OUTCOMES).length;
            const exception = steps.length > 0 && steps.every(s => s.exceptionWork);
            const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
            const what = steps.length ? count(steps.length, 'step', 'steps')
                : [outputs ? count(outputs, 'output', 'outputs') : '', outcomes ? count(outcomes, 'outcome', 'outcomes') : ''].filter(p => p).join(' and ');
            // the share of what leaves the step that goes its way. A step making several pieces of information sends
            // all its items along each, so this is of the items leaving it, not of its own
            const leaves = from ? outgoing.get(from._key) || [] : [];
            const total = leaves.reduce((t, e) => t + (e.items || 0), 0);
            const share = total ? Math.round(100 * leaves.filter(e => groupOfKey.get(e.to) === key).reduce((t, e) => t + (e.items || 0), 0) / total) : 0;
            const name = from ? `${exception ? 'Exception work' : 'Other routes'} from ${from.name}: ${what}${share ? `, ${share}% of what leaves it` : ''}`
                : into ? `Routes into ${into.name}: ${what}` : `Other work: ${what}`;
            const lead = steps[0] || members[0];
            result.push(groupOf(key, name, lead.modelKey, members, lead.type, !from && !!steps.some(s => s.isStarting)));
        });
    });

    const modelOf = new Map(result.map(n => [n._key, n.modelKey]));
    return { nodes: result, edges: edgesBetween(edges, key => groupOfKey.get(key), key => modelOf.get(key)!) };
};

// the step graph of the models shown, as each step or as each model's main flow. The model level is drawn from the
// joins between models instead, as the organisation's model diagram is (see the analysis page)
export const graphAtLevel = (data: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, level: Exclude<GraphLevel, 'models'>, models: { _key: string }[]) => {
    const shown = new Set(models.map(m => m._key));
    const nodes = [...new Map(data.nodes.filter(n => shown.has(n.modelKey)).map(n => [n._key, n])).values()];
    const keys = new Set(nodes.map(n => n._key));
    const edges = data.edges.filter(e => keys.has(e.from) && keys.has(e.to));
    if (level === 'flow') return mainFlowLevel(nodes, edges);
    return { nodes, edges };
};

// the organisation's joins between the models shown, as the home screen's model diagram draws them: each join or
// used-in edge from the information shared to the model it goes into. Joins from or into models not shown are left out
export const joinsBetween = (joins: GraphData, modelKeys: string[]): GraphData => {
    const shown = new Set(modelKeys);
    const modelOf = new Map((joins.nodes || []).map(n => [n._key, n.modelKey]));
    const edges = (joins.edges || []).filter(e => (e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN)
        && shown.has(modelOf.get(e.sourceKey) || '') && shown.has(e.modelKey) && modelOf.get(e.sourceKey) !== e.modelKey);
    const used = new Set(edges.map(e => e.sourceKey));
    return { ...joins, nodes: (joins.nodes || []).filter(n => used.has(n._key)), edges };
};

// the node drawn for a step at a level: the step itself, or the node standing for it
export const nodeShowing = (key: string, nodes: AnalysisNode[]) =>
    nodes.find(n => n._key === key) || nodes.find(n => isGrouped(n) && n.members.some(m => m._key === key));

// a model not shown, drawn where the models shown join it or use its information, or it theirs
export interface OtherModelNode extends AnalysisNode {
    otherModel: true;
}

export const isOtherModel = (node: AnalysisNode | undefined): node is OtherModelNode => (node as OtherModelNode | undefined)?.otherModel === true;

// the step graph with a node for each model not shown that the models shown join or use, or that joins or uses them,
// connected to the nodes holding the steps at either end. A model's connections to a node are made one, carrying what
// all of them carried
export const withOtherModels = (data: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, graph: { nodes: AnalysisNode[]; edges: AnalysisEdge[] },
    models: { _key: string }[], modelName: (key: string) => string | undefined) => {
    const shown = new Set(models.map(m => m._key));
    const byKey = new Map(data.nodes.map(n => [n._key, n]));
    // the node drawn for each step: the step itself, or the node gathering it
    const drawn = new Map<string, string>();
    graph.nodes.forEach(n => {
        drawn.set(n._key, n._key);
        if (isGrouped(n)) n.members.forEach(m => drawn.set(m._key, n._key));
    });
    const others = new Map<string, OtherModelNode>();
    const other = (modelKey: string) => {
        const name = modelName(modelKey);
        if (!name) return undefined;
        let node = others.get(modelKey);
        if (!node) {
            node = {
                _key: `model:${modelKey}`, modelKey, name, otherModel: true, roles: [], systems: [], isStarting: false,
                durationSeconds: 0, numPeople: 0, value: 0, items: 0, capacity: 0, hours: 0, revenue: 0, cost: 0, carbon: 0,
                totalHours: 0, totalCost: 0, totalCarbon: 0, outputsSupported: 0, friction: 0, userValue: 0, userRevenue: 0,
                derivedValue: 0, consumers: 0, consumerModels: 0, outcomesReached: 0, externalConsumers: 0, exceptionWork: false,
                onDetour: false, detourShare: 0, centrality: 0,
            } as unknown as OtherModelNode;
            others.set(modelKey, node);
        }
        return node._key;
    };
    const ends = new Map<string, string>();
    const crossing = data.edges.filter(e => {
        const from = byKey.get(e.from), to = byKey.get(e.to);
        if (!from || !to || shown.has(from.modelKey) === shown.has(to.modelKey)) return false;
        const source = shown.has(from.modelKey) ? drawn.get(e.from) : other(from.modelKey);
        const target = shown.has(to.modelKey) ? drawn.get(e.to) : other(to.modelKey);
        if (!source || !target) return false;
        ends.set(e.from, source);
        ends.set(e.to, target);
        return true;
    });
    if (!others.size) return graph;
    const modelOf = new Map([...graph.nodes, ...others.values()].map(n => [n._key, n.modelKey]));
    return {
        nodes: [...graph.nodes, ...others.values()],
        edges: [...graph.edges, ...edgesBetween(crossing, key => ends.get(key), key => modelOf.get(key)!)],
    };
};
